"use client";

import cx from "classnames";
import React from "react";

import { useStatic } from "../../utils/hooks";

import {
  buildCountdownConfig,
  defaultCountdownTagLabels,
  getAriaLive,
  getLabelText,
  normalizeUnits,
} from "./Countdown.react";
import type {
  CountdownConfig,
  CountdownLabels,
  CountdownUnit,
} from "./Countdown.static";
import CountdownTagStatic from "./CountdownTag.static";
import {
  ATTR_COUNTDOWN_CONFIG,
  ATTR_COUNTDOWN_TAG,
  ATTR_COUNTDOWN_TAG_VALUE,
  ATTR_COUNTDOWN_UNIT,
  CLASS_ANNOUNCEMENT,
  CLASS_TAG_LABEL,
  CLASS_TAG_PART,
  CLASS_TAG_ROOT,
  CLASS_TAG_SEPARATOR,
  CLASS_TAG_VALUE,
} from "./constants";

export interface CountdownTagProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  labels?: CountdownLabels;
  targetDate: string;
  units?: CountdownUnit[];
}

const CountdownTag: React.FC<CountdownTagProps> = ({
  className,
  labels,
  targetDate,
  units = ["days", "hours", "minutes", "seconds"],
  ...other
}) => {
  const normalizedUnits = normalizeUnits(units);
  const config: CountdownConfig = buildCountdownConfig(
    targetDate,
    normalizedUnits,
    labels,
  );

  const [elementRef] = useStatic(CountdownTagStatic, config);

  return (
    <div
      className={cx(CLASS_TAG_ROOT, className)}
      {...{ [ATTR_COUNTDOWN_TAG]: "true" }}
      {...{ [ATTR_COUNTDOWN_CONFIG]: JSON.stringify(config) }}
      ref={elementRef}
      role="timer"
      aria-live={getAriaLive(normalizedUnits)}
      aria-atomic="true"
      {...other}
    >
      <span className={`sr-only ${CLASS_ANNOUNCEMENT}`} />
      {normalizedUnits.map((unit, index) => (
        <React.Fragment key={unit}>
          <span
            className={CLASS_TAG_PART}
            {...{ [ATTR_COUNTDOWN_UNIT]: unit }}
            aria-hidden="true"
          >
            <span
              className={CLASS_TAG_VALUE}
              {...{ [ATTR_COUNTDOWN_TAG_VALUE]: "true" }}
            >
              00
            </span>
            <span className={CLASS_TAG_LABEL}>
              {getLabelText(unit, labels, defaultCountdownTagLabels)}
            </span>
          </span>
          {index < normalizedUnits.length - 1 && (
            <span className={CLASS_TAG_SEPARATOR} aria-hidden="true">
              :
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

CountdownTag.displayName = "CountdownTag";

export { CountdownTag };
